import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../api';
import { useAuth, useBranding } from '../App';
import PasswordField from '../components/PasswordField';
import RecoveryCodesModal from '../components/RecoveryCodesModal';
import { ConfirmModal } from '../components/Modals';
import { useIsMobile } from '../utils';

const NOTIF_DEFAULTS = {
  channel: 'email', notify_job_error: true, notify_job_warning: true,
  notify_daily_summary: true, notify_audit_events: false,
  notify_job_ok: false, notify_storage_alert: true,
  telegram_chat_id: '', email: '',
};

function Field({ label, type = 'text', value, onChange, placeholder = '' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
    </div>
  );
}

function Btn({ label, accent, danger, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: accent ? 'var(--accent)' : danger ? 'var(--red)' : 'transparent',
      border: `1px solid ${accent ? 'var(--accent)' : danger ? 'var(--red)' : 'var(--border)'}`,
      color: (accent || danger) ? (danger ? 'white' : 'var(--bg)') : 'var(--text)',
      padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)',
      cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: (accent || danger) ? 700 : 400,
      opacity: disabled ? 0.5 : 1, marginRight: 8,
    }}>{label}</button>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 20 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)' }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function Toggle({ k, label, form, set }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <div onClick={() => set(k, !form[k])} style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0, background: form[k] ? 'var(--accent)' : 'var(--border)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s', left: form[k] ? 18 : 2 }} />
      </div>
    </div>
  );
}

export default function Profile() {
  const isMobile = useIsMobile();
  const { me, isAdmin, isReseller, resellerPerms } = useAuth();
  // #210 etapa 2: el reseller ve y cambia el branding de su empresa sólo con can_manage_branding
  const canBranding = isAdmin || (isReseller && !!resellerPerms?.can_manage_branding);
  const { setBranding, applyBranding } = useBranding();

  const [saved, setSaved] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const showSaved = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 2500); };
  const showError = (msg) => { setErrMsg(msg); setTimeout(() => setErrMsg(''), 4000); };

  // Display name state
  const [displayName, setDisplayName] = useState(me?.display_name || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA state
  const [totpEnabled, setTotpEnabled] = useState(me?.totp_enabled || false);
  const [mfaSetupMode, setMfaSetupMode] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaUri, setMfaUri] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaSuccess, setMfaSuccess] = useState('');

  // Códigos de recuperación de MFA (#122)
  const [recoveryStatus, setRecoveryStatus] = useState(null);
  const [recoveryCodesModal, setRecoveryCodesModal] = useState(null);
  const [regenCode, setRegenCode] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState('');

  // Notification prefs state
  const [notifForm, setNotifForm] = useState(NOTIF_DEFAULTS);
  const [notifLoading, setNotifLoading] = useState(true);
  const [savingNotif, setSavingNotif] = useState(false);

  // Branding loading
  const [savingBranding, setSavingBranding] = useState(false);
  const setNotif = (k, v) => setNotifForm(f => ({ ...f, [k]: v }));

  // Branding state
  const [brandingForm, setBrandingForm] = useState({ platform_name: '', logo_url: '', favicon_url: '' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  useEffect(() => {
    if (canBranding) {
      api.getBrandingMe().then(b => setBrandingForm(b)).catch(() => {});
    }
  }, [canBranding]);

  useEffect(() => {
    if (me?.id) {
      api.getNotificationPrefs(me.id)
        .then(p => setNotifForm({ ...NOTIF_DEFAULTS, ...p, telegram_chat_id: p.telegram_chat_id || '', email: p.email || '' }))
        .catch(() => {})
        .finally(() => setNotifLoading(false));
    }
  }, [me?.id]); // eslint-disable-line

  useEffect(() => {
    if (totpEnabled) {
      api.mfaRecoveryCodesStatus().then(setRecoveryStatus).catch(() => {});
    }
  }, [totpEnabled]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.updateMe({ display_name: displayName.trim() || null });
      showSaved('Perfil guardado correctamente');
    } catch (e) {
      showError(e.message || 'Error al guardar el perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // #107: cierra todas las sesiones, la actual incluida: la consola vuelve al login con su explicación
  const revokeMySessions = async () => {
    setConfirmRevoke(false);
    try {
      await api.revokeMySessions();
      try { sessionStorage.setItem('logout_reason', 'revoked'); } catch (e) { /* sin storage */ }
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (e) {
      showError(e.message || 'Error al cerrar las sesiones');
    }
  };

  const changePassword = async () => {
    if (!currentPassword) { showError('Ingresá la contraseña actual'); return; }
    if (newPassword.length < 8) { showError('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (newPassword !== confirmPassword) { showError('Las contraseñas no coinciden'); return; }
    setChangingPassword(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showSaved('Contraseña actualizada correctamente. Listo. Se cerraron tus otras sesiones.');
    } catch (e) {
      showError(e.message || 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  const startMfaSetup = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const r = await api.mfaSetup();
      setMfaSecret(r.secret);
      setMfaUri(r.uri);
      setMfaSetupMode(true);
    } catch (e) { setMfaError(e.message); }
    finally { setMfaLoading(false); }
  };

  const confirmMfa = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const r = await api.mfaEnable(mfaSecret, mfaCode);
      setTotpEnabled(true);
      setMfaSetupMode(false);
      setMfaCode('');
      setMfaSuccess('MFA activado correctamente.');
      if (r.recovery_codes) setRecoveryCodesModal(r.recovery_codes);
    } catch (e) { setMfaError(e.message); }
    finally { setMfaLoading(false); }
  };

  const regenerateRecoveryCodes = async () => {
    setRegenError('');
    setRegenLoading(true);
    try {
      const r = await api.mfaRecoveryCodesRegenerate(regenCode);
      setRegenCode('');
      setRecoveryCodesModal(r.codes);
      showSaved('Listo. Se cerraron tus otras sesiones.');
    } catch (e) { setRegenError(e.message); }
    finally { setRegenLoading(false); }
  };

  const closeRecoveryCodesModal = () => {
    setRecoveryCodesModal(null);
    api.mfaRecoveryCodesStatus().then(setRecoveryStatus).catch(() => {});
  };

  const disableMfa = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      await api.mfaDisable(mfaCode);
      setTotpEnabled(false);
      setMfaCode('');
      setMfaSuccess('MFA desactivado. Listo. Se cerraron tus otras sesiones.');
    } catch (e) { setMfaError(e.message); }
    finally { setMfaLoading(false); }
  };

  const saveNotifPrefs = async () => {
    setSavingNotif(true);
    try {
      await api.updateNotificationPrefs(me.id, {
        ...notifForm,
        telegram_chat_id: notifForm.telegram_chat_id || null,
        email: notifForm.email || null,
      });
      showSaved('Preferencias de notificación guardadas');
    } catch (e) { showError(e.message); }
    finally { setSavingNotif(false); }
  };

  const saveBrandingName = async () => {
    setSavingBranding(true);
    try {
      if (isAdmin) {
        await api.updateSettings({ platform_name: brandingForm.platform_name });
      } else if (isReseller) {
        await api.updateResellerBranding({ branding_name: brandingForm.platform_name });
      }
      const updated = { ...brandingForm };
      setBranding(updated);
      applyBranding(updated);
      showSaved('Nombre de plataforma guardado');
    } catch (e) { showError(e.message); }
    finally { setSavingBranding(false); }
  };

  const handleUpload = async (assetType, file) => {
    if (assetType === 'logo')    setUploadingLogo(true);
    if (assetType === 'favicon') setUploadingFavicon(true);
    try {
      const res = await api.uploadAsset(assetType, file);
      const updated = { ...brandingForm, [`${assetType}_url`]: res.url };
      setBrandingForm(updated);
      setBranding(updated);
      applyBranding(updated);
      showSaved(`${assetType === 'logo' ? 'Logo' : 'Favicon'} actualizado`);
    } catch (e) { showError(e.message); }
    finally {
      setUploadingLogo(false);
      setUploadingFavicon(false);
    }
  };

  // Sin Chat ID el backend rechaza telegram/both con un 400 (update_notification_prefs).
  const telegramSinDestino = (notifForm.channel === 'telegram' || notifForm.channel === 'both') && !notifForm.telegram_chat_id;

  const roleLabel = { admin: 'ADMIN', operator: 'OPERADOR', client: 'CLIENTE', reseller: 'RESELLER' };
  const roleColor = { admin: 'var(--red)', operator: 'var(--accent)', client: 'var(--green)', reseller: 'var(--yellow)' };

  const inpStyle = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', boxSizing: 'border-box' };
  const lblStyle = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--mono)' };

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Mi Perfil</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{me?.email}</div>
        </div>
        <span style={{
          padding: '3px 10px', fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 2,
          textTransform: 'uppercase', border: '1px solid currentColor',
          color: roleColor[me?.role] || 'var(--muted)',
        }}>
          {roleLabel[me?.role] || me?.role}
        </span>
      </div>

      {errMsg && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          ✕ {errMsg}
        </div>
      )}
      {saved && (
        <div style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)', padding: '10px 16px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 20 }}>
          ✓ {saved}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
        <div>
          <Card title="Perfil">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                Email
              </label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
                {me?.email}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                Nombre
              </label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Tu nombre o alias"
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <Btn
              label={savingProfile ? 'GUARDANDO...' : 'GUARDAR →'}
              accent
              onClick={saveProfile}
              disabled={savingProfile}
            />
          </Card>

          <Card title="Cuenta">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                Contraseña actual
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Contraseña actual"
                style={{
                  width: '100%', padding: '8px 10px',
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <PasswordField
              label="Nueva contraseña"
              value={newPassword}
              onChange={setNewPassword}
            />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                Confirmar nueva contraseña
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${confirmPassword && confirmPassword !== newPassword ? 'var(--red)' : 'var(--border)'}`, color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }}
              />
              {confirmPassword && confirmPassword !== newPassword && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, fontFamily: 'var(--mono)' }}>Las contraseñas no coinciden</div>
              )}
            </div>
            <Btn
              label={changingPassword ? 'GUARDANDO...' : 'CAMBIAR CONTRASEÑA →'}
              accent
              onClick={changePassword}
              disabled={changingPassword}
            />
          </Card>

          <Card title="Sesiones">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
              Cierra la sesión en todos tus navegadores y dispositivos, este incluido. Cambiar la contraseña también las cierra.
            </div>
            <Btn label="CERRAR TODAS MIS SESIONES" danger onClick={() => setConfirmRevoke(true)} />
          </Card>

          <Card title="Autenticación de dos factores">
            {mfaError && (
              <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{mfaError}</div>
            )}
            {mfaSuccess && (
              <div style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{mfaSuccess}</div>
            )}
            {totpEnabled && (
              <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
                {recoveryStatus && recoveryStatus.total === 0 && (
                  <div style={{ background: 'rgba(255,196,0,0.1)', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 12 }}>
                    No tenés códigos de recuperación generados. Si perdés el acceso a tu
                    app autenticadora, no vas a poder recuperar el acceso. Generalos ahora.
                  </div>
                )}
                {recoveryStatus && recoveryStatus.total > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
                    {recoveryStatus.unused} de {recoveryStatus.total} códigos de recuperación sin usar
                  </div>
                )}
                {regenError && (
                  <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 12 }}>{regenError}</div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <label style={lblStyle}>Código actual (para {recoveryStatus?.total > 0 ? 'regenerar' : 'generar'} códigos)</label>
                  <input
                    type="text"
                    value={regenCode}
                    onChange={e => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{ ...inpStyle, letterSpacing: 6, fontSize: 16, textAlign: 'center', maxWidth: isMobile ? '100%' : 160 }}
                  />
                </div>
                <Btn
                  label={regenLoading ? 'GENERANDO...' : (recoveryStatus?.total > 0 ? 'REGENERAR CÓDIGOS' : 'GENERAR CÓDIGOS')}
                  onClick={regenerateRecoveryCodes}
                  disabled={regenLoading || regenCode.length < 6}
                />
              </div>
            )}
            {!totpEnabled && !mfaSetupMode && !mfaSuccess && (
              <div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                  El MFA agrega una capa extra de seguridad usando una app autenticadora.
                </div>
                <Btn label={mfaLoading ? 'CARGANDO...' : 'ACTIVAR MFA'} accent onClick={startMfaSetup} disabled={mfaLoading} />
              </div>
            )}
            {!totpEnabled && mfaSetupMode && (
              <>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                  Escaneá el código QR con Google Authenticator o Microsoft Authenticator.
                </div>
                {mfaUri && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <div style={{ padding: 12, background: '#FFFFFF' }}>
                      <QRCodeSVG
                        value={mfaUri}
                        size={200}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        level="M"
                        marginSize={2}
                        role="img"
                        aria-label="Código QR para configurar MFA"
                      />
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 16 }}>
                  <label style={lblStyle}>Clave manual</label>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', letterSpacing: 2, wordBreak: 'break-all' }}>
                    {mfaSecret}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={lblStyle}>Confirmar con código</label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{ ...inpStyle, letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
                    autoFocus
                  />
                </div>
                <Btn
                  label={mfaLoading ? 'ACTIVANDO...' : 'CONFIRMAR Y ACTIVAR →'}
                  accent
                  onClick={confirmMfa}
                  disabled={mfaLoading || mfaCode.length < 6}
                />
              </>
            )}
            {totpEnabled && !mfaSuccess && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <span style={{ fontSize: 13, color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 700 }}>✓ MFA ACTIVO</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                  Ingresá el código actual de tu app autenticadora para desactivar MFA.
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={lblStyle}>Código de verificación</label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{ ...inpStyle, letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
                  />
                </div>
                <Btn
                  label={mfaLoading ? 'DESACTIVANDO...' : 'DESACTIVAR MFA'}
                  danger
                  onClick={disableMfa}
                  disabled={mfaLoading || mfaCode.length < 6}
                />
              </>
            )}
          </Card>

          <Card title="Notificaciones">
            {notifLoading ? (
              <div style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>CARGANDO...</div>
            ) : (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={lblStyle}>Canal de notificaciones</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['telegram', 'Telegram'], ['email', 'Email'], ['both', 'Ambos']].map(([v, l]) => (
                      <button key={v} onClick={() => setNotif('channel', v)} style={{
                        flex: 1, padding: '8px 0', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer',
                        background: notifForm.channel === v ? 'var(--accent)' : 'transparent',
                        border: `1px solid ${notifForm.channel === v ? 'var(--accent)' : 'var(--border)'}`,
                        color: notifForm.channel === v ? 'var(--bg)' : 'var(--text)', fontWeight: notifForm.channel === v ? 700 : 400,
                      }}>{l}</button>
                    ))}
                  </div>
                </div>
                {(notifForm.channel === 'telegram' || notifForm.channel === 'both') && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={lblStyle}>Chat ID de Telegram</label>
                    <input value={notifForm.telegram_chat_id} onChange={e => setNotif('telegram_chat_id', e.target.value)} placeholder="Obtener con @userinfobot" style={inpStyle} />
                    {!notifForm.telegram_chat_id && (
                      <div style={{ fontSize: 11, color: 'var(--yellow)', marginTop: 6 }}>
                        Sin Chat ID no se puede guardar Telegram como canal — las notificaciones no tendrían destino.
                      </div>
                    )}
                  </div>
                )}
                {(notifForm.channel === 'email' || notifForm.channel === 'both') && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={lblStyle}>Email para notificaciones</label>
                    <input value={notifForm.email} onChange={e => setNotif('email', e.target.value)} placeholder={me?.email} style={inpStyle} />
                  </div>
                )}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8, marginBottom: 16 }}>
                  {(me?.role === 'admin' || me?.role === 'operator') && (
                    <>
                      <Toggle k="notify_job_error"     label="Jobs con error"         form={notifForm} set={setNotif} />
                      <Toggle k="notify_job_warning"   label="Jobs con warning"        form={notifForm} set={setNotif} />
                      <Toggle k="notify_daily_summary" label="Resumen diario"          form={notifForm} set={setNotif} />
                      <Toggle k="notify_audit_events"  label="Eventos de auditoría"    form={notifForm} set={setNotif} />
                    </>
                  )}
                  {me?.role === 'client' && (
                    <>
                      <Toggle k="notify_job_ok"        label="Jobs exitosos"           form={notifForm} set={setNotif} />
                      <Toggle k="notify_job_error"     label="Jobs con error/warning"  form={notifForm} set={setNotif} />
                      <Toggle k="notify_storage_alert" label="Alerta de storage (85%)" form={notifForm} set={setNotif} />
                    </>
                  )}
                  {me?.role === 'reseller' && (
                    <>
                      <Toggle k="notify_job_ok"        label="Jobs exitosos"           form={notifForm} set={setNotif} />
                      <Toggle k="notify_job_error"     label="Jobs con error"          form={notifForm} set={setNotif} />
                      <Toggle k="notify_job_warning"   label="Jobs con warning"        form={notifForm} set={setNotif} />
                      <Toggle k="notify_daily_summary" label="Resumen diario"          form={notifForm} set={setNotif} />
                      <Toggle k="notify_storage_alert" label="Alerta de storage (85%)" form={notifForm} set={setNotif} />
                    </>
                  )}
                </div>
                <Btn label={savingNotif ? 'GUARDANDO...' : 'GUARDAR →'} accent onClick={saveNotifPrefs} disabled={savingNotif || telegramSinDestino} />
              </>
            )}
          </Card>
        </div>

        <div>
          {canBranding && (
            <Card title="Identidad de la Plataforma">
              <Field
                label="Nombre de la plataforma"
                value={brandingForm.platform_name || ''}
                onChange={v => setBrandingForm(f => ({ ...f, platform_name: v }))}
                placeholder="OpenBackup"
              />
              <Btn label={savingBranding ? 'GUARDANDO...' : 'GUARDAR NOMBRE'} accent onClick={saveBrandingName} disabled={savingBranding} />

              <div style={{ marginTop: 20, marginBottom: 8, fontSize: 10, letterSpacing: 2, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>
                Logo del sidebar
              </div>
              {brandingForm.logo_url && (
                <img
                  src={brandingForm.logo_url}
                  alt="Logo actual"
                  style={{ height: 40, marginBottom: 8, objectFit: 'contain', display: 'block' }}
                />
              )}
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                style={{ display: 'none' }}
                id="logo-upload"
                onChange={e => e.target.files[0] && handleUpload('logo', e.target.files[0])}
              />
              <Btn
                label={uploadingLogo ? 'SUBIENDO...' : 'SUBIR LOGO'}
                onClick={() => document.getElementById('logo-upload').click()}
              />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                PNG o JPG · máx. 500KB
              </div>

              <div style={{ marginTop: 20, marginBottom: 8, fontSize: 10, letterSpacing: 2, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>
                Favicon
              </div>
              {brandingForm.favicon_url && (
                <img
                  src={brandingForm.favicon_url}
                  alt="Favicon actual"
                  style={{ height: 24, marginBottom: 8, display: 'block' }}
                />
              )}
              <input
                type="file"
                accept=".ico,.png"
                style={{ display: 'none' }}
                id="favicon-upload"
                onChange={e => e.target.files[0] && handleUpload('favicon', e.target.files[0])}
              />
              <Btn
                label={uploadingFavicon ? 'SUBIENDO...' : 'SUBIR FAVICON'}
                onClick={() => document.getElementById('favicon-upload').click()}
              />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                ICO o PNG · máx. 500KB
              </div>
            </Card>
          )}
        </div>
      </div>

      {confirmRevoke && <ConfirmModal title="Cerrar todas mis sesiones" message="Se cerrarán todas tus sesiones, también esta. Vas a tener que volver a ingresar." danger confirmLabel="CERRAR SESIONES" onConfirm={revokeMySessions} onCancel={() => setConfirmRevoke(false)} />}
      {recoveryCodesModal && (
        <RecoveryCodesModal codes={recoveryCodesModal} onClose={closeRecoveryCodesModal} />
      )}
    </div>
  );
}
